import { Component, Inject, OnInit, ViewChild } from '@angular/core';

import {
  AddLocation,
  ExtraContactsForm,
  ImageCropperInterface,
  LocationForm,
  LocationInterface,
  ModalOutputInterface,
} from '@models';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { UpperCasePipe } from '@angular/common';
import { NameToColorPipe } from '@shared/pipes';
import { ProfileImageComponent } from '@shared/components';
import {
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormArray,
} from '@angular/forms';
import { LocationsModule } from '../../../view/dashboard/pages/locations/locations.module';
import { finalize } from 'rxjs/operators';
import { RouterLinks } from '@constants';
import { BaseApiService } from '@core/api-services';
import { FileUploadHelper } from '@core/helpers';
import {
  LocationsInteractionsService,
  MessageService,
  UtilitiesService,
} from '@services';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { LocationFormComponent } from '../../../view/dashboard/pages/locations/components/location-form/location-form.component';

@Component({
  selector: 'app-add-location-modal',
  imports: [
    MatIconModule,
    ProfileImageComponent,
    MatButtonModule,
    LocationFormComponent,
  ],
  templateUrl: './add-location-modal.component.html',
  styleUrls: ['./add-location-modal.component.scss'],
})
export class AddLocationModalComponent implements OnInit {
  @ViewChild('locationFormComponent') selectedForm: {
    locationForm: FormGroup;
    extraContactsForm: FormArray;
  };
  @ViewChild('locationImageComponent') imageForm: {
    imageControl: FormControl;
    getData(): ImageCropperInterface;
  };

  public pdfFile: {
    pdf: File | null;
    pdf_deleted: boolean;
  };
  public pdf: {
    url: string;
    name: string;
  };
  public loading: boolean;
  private _imageFile = { image: null, image_deleted: false };

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      isEdit: boolean;
      locationData: LocationForm;
      locationId: string;
    },
    private _dialogRef: MatDialogRef<
      AddLocationModalComponent,
      ModalOutputInterface
    >,
    private _baseApiService: BaseApiService,
    private _locationService: LocationsInteractionsService,
    private _messageService: MessageService,
    private _utilitiesService: UtilitiesService,
    private _router: Router,
  ) {}

  ngOnInit() {}

  public onClose() {
    this._dialogRef.close();
  }

  public saveData(): void {
    this.loading = true;
    if (
      this.selectedForm.locationForm.invalid ||
      this.selectedForm.extraContactsForm.invalid
    ) {
      this.loading = false;
      this.selectedForm.locationForm.markAllAsTouched();
      this.selectedForm.extraContactsForm.markAllAsTouched();
      return;
    }
    this._imageFile = this.imageForm.getData();
    const formData: FormData = this.prepareLocationData();

    this._baseApiService.locationsService
      .saveLocation(this.data.locationId, formData)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe(async (resp: LocationInterface) => {
        if (this.pdfFile && this.pdfFile.pdf) {
          const uploadedData = await this.uploadPdf(resp.location_id);
          resp.location_info = uploadedData.location_info;
        }

        this._dialogRef.close({ continue: true, output: null });
        this._router.navigate([RouterLinks.LOCATIONS]);
      });
  }

  private prepareLocationData(): FormData {
    this.removeEmptyInputs();
    const extraContacts = this.getStringifiedExtraContacts();
    const formValues = this.selectedForm.locationForm.getRawValue();
    const { email, ...trimmedObject } =
      this._utilitiesService.trimObjectValues<LocationForm>(formValues);
    const data: AddLocation = {
      ...trimmedObject,
      email: email.toLowerCase(),
      file: this._imageFile.image,
      pdf_deleted: this.pdfFile?.pdf_deleted,
      extra_contacts: extraContacts,
      image_deleted: this._imageFile.image_deleted,
    };
    return FileUploadHelper.toFormData(data);
  }

  private removeEmptyInputs(): void {
    const contactForm = this.selectedForm.extraContactsForm.get(
      'extra_contacts',
    ) as UntypedFormArray;
    for (let i = contactForm.getRawValue().length - 1; i >= 0; i--) {
      if (
        !contactForm.at(i).value.contact_phone_number &&
        !contactForm.at(i).value.contact_name &&
        !contactForm.at(i).value.contact_email
      ) {
        contactForm.removeAt(i);
      }
    }
  }

  private async uploadPdf(
    locationId: string,
  ): Promise<Pick<LocationInterface, 'location_info'>> {
    const locationFiles = await this._locationService.startPdfUpload(
      locationId,
      this.pdfFile.pdf,
    );
    if (!locationFiles) {
      this._messageService.openConfirmModal({
        msg: 'PDF upload failed',
        isConfirm: false,
      });
      return;
    }
    return locationFiles;
  }

  private getStringifiedExtraContacts(): string {
    const extraContactsForm = (
      this.selectedForm.extraContactsForm.get('extra_contacts') as FormArray
    )
      .getRawValue()
      .map((extraContacts: ExtraContactsForm) => {
        return this._utilitiesService.trimObjectValues<ExtraContactsForm>(
          extraContacts,
        );
      });
    return JSON.stringify(extraContactsForm);
  }

  public pdfUpload(event): void {
    const file = event.target.files[0];
    if (!file || !FileUploadHelper.checkExtension(file.type, ['.pdf'])) return;

    this.pdfFile = file;
    this.data.locationData.pdf_name = event.target.files[0]?.name;
  }

  public removePdf() {
    this.pdfFile = null;
    this.data.locationData.pdf = '';
    this.data.locationData.pdf_name = '';
  }
}
